// themeService.js - Light/Dark theme management with CSS variables and persistence
import { reactive, computed, onMounted } from 'vue'

const STORAGE_KEY = 'lyt_theme_preference'

const state = reactive({
  mode: 'light', // 'light' | 'dark' | 'system'
  isDark: false,
})

function getSystemPrefersDark() {
  if (typeof window === 'undefined') return false
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
}

function applyTheme(isDark) {
  const root = document.documentElement
  root.setAttribute('data-theme', isDark ? 'dark' : 'light')
  // Do not set CSS variables inline on <html>; global CSS handles both themes.
}

function loadPreference() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

function savePreference() {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ mode: state.mode })
  )
}

function updateFromMode() {
  const isDark = state.mode === 'system' ? getSystemPrefersDark() : state.mode === 'dark'
  state.isDark = isDark
  applyTheme(isDark)
}

export function useTheme() {
  onMounted(() => {
    const saved = loadPreference()
    if (saved && (saved.mode === 'light' || saved.mode === 'dark' || saved.mode === 'system')) {
      state.mode = saved.mode
    } else {
      state.mode = 'light'
    }
    updateFromMode()

    // Watch system changes when in system mode
    if (typeof window !== 'undefined') {
      const media = window.matchMedia('(prefers-color-scheme: dark)')
      const handler = () => {
        if (state.mode === 'system') updateFromMode()
      }
      if (media.addEventListener) media.addEventListener('change', handler)
      else if (media.addListener) media.addListener(handler)
    }
  })

  const currentTheme = computed(() => (state.isDark ? 'dark' : 'light'))
  const isDark = computed(() => state.isDark)
  const mode = computed(() => state.mode)

  function setMode(newMode) {
    if (!['light', 'dark', 'system'].includes(newMode)) return
    state.mode = newMode
    savePreference()
    updateFromMode()
  }

  function toggle() {
    setMode(state.isDark ? 'light' : 'dark')
  }

  return { currentTheme, isDark, mode, setMode, toggle }
}

// Initialize theme as early as possible (without relying on component lifecycle)
export function initThemeEarly() {
  try {
    const saved = loadPreference()
    if (saved && (saved.mode === 'light' || saved.mode === 'dark' || saved.mode === 'system')) {
      state.mode = saved.mode
    } else {
      state.mode = 'light'
    }
    updateFromMode()

    if (typeof window !== 'undefined') {
      const media = window.matchMedia('(prefers-color-scheme: dark)')
      const handler = () => {
        if (state.mode === 'system') updateFromMode()
      }
      if (media.addEventListener) media.addEventListener('change', handler)
      else if (media.addListener) media.addListener(handler)
    }
  } catch {}
}

// Public setter for external callers (e.g., Vuex/actions) to persist and apply
export function setThemeMode(mode) {
  if (!['light', 'dark', 'system'].includes(mode)) return
  state.mode = mode
  savePreference()
  updateFromMode()
}

/**
 * Install global debug helper for theme testing
 *
 * Usage in DevTools console:
 * - window.__forceTheme('dark')
 * - window.__forceTheme('light')
 * - window.__forceTheme('system')
 */
export function installThemeDebugHelper() {
  if (typeof window === 'undefined') return

  window.__forceTheme = (mode) => {
    try {
      if (!['light', 'dark', 'system'].includes(mode)) {
        console.warn('Invalid theme mode. Use: "light", "dark", or "system"')
        return
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify({ mode }))

      const isDark = mode === 'system'
        ? getSystemPrefersDark()
        : mode === 'dark'

      document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light')

      console.log(`✅ Theme set to: ${mode}`)
    } catch (err) {
      console.error('Failed to set theme:', err)
    }
  }
}
